import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { REACT_APP_API_URL } from "../../../../../env";
import "bootstrap-icons/font/bootstrap-icons.css";

const customStyles = {
    rows: {
        style: {
            minHeight: "45px",
            borderBottom: "1px solid #dee2e6",
        },
    },
    headCells: {
        style: {
            fontWeight: "bold",
            fontSize: "14px",
            border: "1px solid #dee2e6",
            backgroundColor: "#f8f9fa",
            position: "sticky",
            top: 0,
            zIndex: 1,
            whiteSpace: "nowrap",
            overflow: "visible",
            textOverflow: "clip",
        },
    },
    headRow: {
        style: {
            position: "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor: "#f8f9fa",
        },
    },
    cells: {
        style: {
            border: "1px solid #dee2e6",
        },
    },
};

const normalizeKey = (key) => String(key || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const parseNotes = (notes) => {
    if (!notes) return null;
    if (typeof notes === "object") return notes;
    if (typeof notes === "string") {
        try {
            const parsed = JSON.parse(notes);
            return typeof parsed === "object" && parsed !== null ? parsed : { rawNotes: notes };
        } catch {
            return { rawNotes: notes };
        }
    }
    return { rawNotes: String(notes) };
};

const findValueByKeys = (source, keys) => {
    const targetKeys = new Set(keys.map(normalizeKey));
    const stack = [source];

    while (stack.length) {
        const current = stack.pop();
        if (!current || typeof current !== "object") continue;

        if (Array.isArray(current)) {
            current.forEach((item) => stack.push(item));
            continue;
        }

        for (const [key, value] of Object.entries(current)) {
            if (value && typeof value === "object") {
                stack.push(value);
            }

            if (value !== null && value !== undefined && value !== "" && targetKeys.has(normalizeKey(key))) {
                return value;
            }
        }
    }

    return "";
};

const getNotesDisplayItems = (row) => {
    const notes = parseNotes(row.notes);
    const bookingId = findValueByKeys(notes, ["bookingId", "booking_id", "bookId"]);
    const bookingRef = findValueByKeys(notes, ["bookingRef", "booking_ref", "bookingRefNo", "BookingRefNo", "refNo"]);

    const items = [];
    if (bookingId) items.push({ label: "Booking ID", value: bookingId });
    if (bookingRef) items.push({ label: "Booking Ref", value: bookingRef });

    if (!items.length && notes && typeof notes === "object") {
        Object.entries(notes).forEach(([key, value]) => {
            if (items.length >= 2) return;
            if (value !== null && value !== undefined && typeof value !== "object" && value !== "") {
                items.push({ label: key, value });
            }
        });
    }

    return items;
};

const getRowSearchText = (item) => {
    const notes = parseNotes(item.notes);
    const notesText = notes ? JSON.stringify(notes) : "";

    return [
        item.id,
        item.refund_id,
        item.payment_id,
        item.booking_id,
        item.booking_ref_no,
        item.BookingRefNo,
        notesText,
    ]
        .filter((value) => value !== null && value !== undefined)
        .map((value) => String(value).toLowerCase())
        .join(" ");
};

const RefundList = () => {
    const [data, setData] = useState([]);
    const [pending, setPending] = useState(true);
    const [error, setError] = useState(null);
    const [filterFrom, setFilterFrom] = useState("");
    const [filterTo, setFilterTo] = useState("");
    const [searchRefundId, setSearchRefundId] = useState("");
    const [dateError, setDateError] = useState("");

    useEffect(() => {
        fetch(`${REACT_APP_API_URL}api/refund/all`, { credentials: "include" })
            .then((res) => {
                if (!res.ok) throw new Error("Failed to fetch refund list");
                return res.json();
            })
            .then((result) => {

                if (result.success || Array.isArray(result) || result.refunds) {

                    let items = result.items || result.data || result.refunds || result;
                    if (Array.isArray(items)) {
                        setData(items);
                    }
                } else {
                    setError("No data received or success false");
                }
            })
            .catch((err) => setError(err.message))
            .finally(() => setPending(false));
    }, []);

    const handleExcelExport = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "RefundList");
        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, "RefundList.xlsx");
    };

    const handlePdfExport = () => {
        const doc = new jsPDF();
        const head = [columns.map((col) => col.name)];
        const body = filteredData.map((row) =>
            columns.map((col) =>
                typeof col.selector === "function" ? col.selector(row) : ""
            )
        );
        autoTable(doc, {
            head,
            body,
            styles: { fontSize: 8 },
            margin: { top: 10 },
        });
        doc.save("RefundList.pdf");
    };

    // Date change handlers
    const handleFromDateChange = (e) => {
        const value = e.target.value;
        setFilterFrom(value);
        if (filterTo && value && new Date(value) > new Date(filterTo)) {
            setDateError("From date cannot be after To date.");
        } else {
            setDateError("");
        }
    };

    const handleToDateChange = (e) => {
        const value = e.target.value;
        setFilterTo(value);
        if (filterFrom && value && new Date(filterFrom) > new Date(value)) {
            setDateError("From date cannot be after To date.");
        } else {
            setDateError("");
        }
    };

    const columns = [
        {
            name: "Created At",
            selector: (row) => row.created_at ? new Date(row.created_at * 1000).toLocaleString() : '',
            sortable: true,
            minWidth: "180px",
        },
        {
            name: "Refund ID",
            selector: (row) => row.id || row.refund_id,
            sortable: true,
            minWidth: "180px",
        },

        {
            name: "Payment ID",
            selector: (row) => row.payment_id,
            sortable: true,
            minWidth: "180px",
        },
        {
            name: "Amount",
            selector: (row) => (row.amount / 100).toFixed(2),
            sortable: true,
            right: true,
            minWidth: "120px",
        },
        {
            name: "Status",
            selector: (row) => row.status,
            sortable: true,
            minWidth: "120px",
            cell: (row) => (
                <span className={`badge bg-${row.status === 'processed' ? 'success' : 'warning'}`}>
                    {row.status}
                </span>
            )
        },
        {
            name: "Details",
            minWidth: "220px",
            cell: (row) => {
                const detailItems = getNotesDisplayItems(row);

                return (
                    <div className="d-flex flex-column">
                        {detailItems.length ? (
                            detailItems.map((item, index) => (
                                <small key={`${item.label}-${index}`}>
                                    <strong>{item.label}:</strong> {String(item.value)}
                                </small>
                            ))
                        ) : (
                            <small>-</small>
                        )}
                    </div>
                );
            },
        }
    ];

    const filteredData = useMemo(() => {
        if (dateError) return [];
        return data.filter((item) => {
            const itemDate = item.created_at ? new Date(item.created_at * 1000) : null;
            const fromDate = filterFrom ? new Date(filterFrom) : null;
            const toDate = filterTo ? new Date(filterTo) : null;

            const matchesFrom = fromDate && itemDate ? itemDate >= fromDate : true;
            const matchesTo = toDate && itemDate ? itemDate <= toDate : true;
            const searchText = searchRefundId.toLowerCase();
            const rowSearchText = getRowSearchText(item);
            const matchesSearch = !searchText || rowSearchText.includes(searchText);

            return matchesFrom && matchesTo && matchesSearch;
        });
    }, [data, filterFrom, filterTo, searchRefundId, dateError]);

    return (
        <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
            <div className="row g-3 mb-3">
                <div className="col-12 d-flex flex-wrap justify-content-between align-items-center">
                    <h2 className="mb-0">Refunded List</h2>
                </div>
            </div>

            {dateError && (
                <div className="col-12">
                    <div className="alert alert-danger py-2">{dateError}</div>
                </div>
            )}

            <div className="row g-3">
                <div className="col-12">
                    <div className="card shadow-sm rounded-1">
                        <div className="card-body">
                            {error && <div className="alert alert-danger">{error}</div>}
                            <div className="d-flex flex-wrap gap-3 align-items-end mt-5 mb-5 p-3 border rounded">
                                <div className="d-flex flex-column">
                                    <label className="form-label mb-1">From Date</label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={filterFrom}
                                        onChange={handleFromDateChange}
                                        max={filterTo || undefined}
                                    />
                                </div>

                                <div className="d-flex flex-column">
                                    <label className="form-label mb-1">To Date</label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={filterTo}
                                        onChange={handleToDateChange}
                                        min={filterFrom || undefined}
                                    />
                                </div>

                                <div className="d-flex flex-column">
                                    <label className="form-label mb-1">Search ID</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Refund / Payment / Booking / Notes"
                                        value={searchRefundId}
                                        onChange={(e) => setSearchRefundId(e.target.value)}
                                    />
                                </div>

                                <div className="d-flex flex-column">
                                    <label className="form-label mb-1">Export</label>
                                    <div className="d-flex gap-2">
                                        <CSVLink
                                            data={filteredData}
                                            filename="RefundList.csv"
                                            className="btn btn-sm btn-outline-primary"
                                            title="Export CSV"
                                        >
                                            <i className="bi bi-file-earmark"></i>
                                        </CSVLink>

                                        <button
                                            className="btn btn-sm btn-outline-success"
                                            onClick={handleExcelExport}
                                            title="Export Excel"
                                        >
                                            <i className="bi bi-file-earmark-excel"></i>
                                        </button>

                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={handlePdfExport}
                                            title="Export PDF"
                                        >
                                            <i className="bi bi-filetype-pdf"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <DataTable
                                columns={columns}
                                data={filteredData}
                                pagination
                                paginationPerPage={100}
                                paginationRowsPerPageOptions={[50, 100, 500]}
                                responsive
                                striped
                                highlightOnHover
                                progressPending={pending}
                                customStyles={customStyles}
                                noDataComponent="No refunds found."
                                fixedHeader
                                fixedHeaderScrollHeight="500px"
                            />

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RefundList;
